import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import SmartToyRoundedIcon from "@mui/icons-material/SmartToyRounded";
import type {RonaldTask} from "./RonaldStatusStrip";

interface RonaldAuditCardProps {
    task: RonaldTask;
}

function formatTimestamp(value?: string | null): string {
    if (!value) return "";
    try {
        const date = new Date(value);
        return date.toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return value;
    }
}

function taskTypeLabel(task: RonaldTask): string {
    if (task.task_type === "FOLLOW_UP") return "Follow-up";
    if (task.task_type === "SEQUENCE_STEP") return "Sequence Step";
    return "Message Reply";
}

const ACTION_LABELS: Record<string, string> = {
    cancel_booking: "Cancelled Booking",
    mark_needs_rescheduling: "Flagged for Rescheduling",
};

function resolveBadge(task: RonaldTask): {
    label: string;
    bg: string;
    text: string;
    borderColor: string;
    cardBg: string;
} {
    const reason = (task.cancel_reason || "").toLowerCase();

    if (task.status === "COMPLETED" && task.action_taken) {
        return {
            label: "ACTION",
            bg: "#f3e8ff",    // purple-100
            text: "#7c3aed",  // purple-600
            borderColor: "#d8b4fe", // purple-300
            cardBg: "#faf5ff", // purple-50
        };
    }
    if (task.status === "HUMAN_INTERVENED") {
        return {
            label: "NEEDS HUMAN",
            bg: "#fef3c7",    // amber-100
            text: "#b45309",  // amber-700
            borderColor: "#fcd34d", // amber-300
            cardBg: "#fffbeb", // amber-50
        };
    }
    if (reason.includes("failed") || reason.includes("retry")) {
        return {
            label: "FAILED",
            bg: "#fee2e2",    // red-100
            text: "#b91c1c",  // red-700
            borderColor: "#fecaca", // red-200
            cardBg: "#fef2f2", // red-50
        };
    }
    return {
        label: "CANCELLED",
        bg: "#e2e8f0",    // slate-200
        text: "#475569",  // slate-600
        borderColor: "#cbd5e1", // slate-300
        cardBg: "rgba(241,245,249,0.95)", // slate-100/95
    };
}

export default function RonaldAuditCard({task}: RonaldAuditCardProps) {
    const badge = resolveBadge(task);
    const timestamp = formatTimestamp(task.cancelled_at || task.updated_at || task.created_at);
    const showIntervention =
        task.status === "HUMAN_INTERVENED" && !!task.intervention_reason;
    const actionLabel = task.action_taken ? ACTION_LABELS[task.action_taken] || task.action_taken : null;

    return (
        <Box sx={{width: "100%", py: 0.5}}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1.5}}>
                {/* Left line */}
                <Box sx={{flex: 1, height: "1px", bgcolor: "#e2e8f0"}}/>

                {/* Center card */}
                <Box
                    sx={{
                        flexShrink: 0,
                        borderRadius: "xl",
                        border: "1px solid",
                        borderColor: badge.borderColor,
                        bgcolor: badge.cardBg,
                        px: 1.5,
                        py: 1,
                        boxShadow: "sm",
                        textAlign: "center",
                    }}
                >
                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 1}}>
                        <SmartToyRoundedIcon sx={{fontSize: 14, color: "#64748b"}} aria-hidden/>
                        <Box
                            component="span"
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                borderRadius: "9999px",
                                px: 0.75,
                                py: "2px",
                                fontSize: "10px",
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                bgcolor: badge.bg,
                                color: badge.text,
                            }}
                        >
                            {badge.label}
                        </Box>
                        <Typography level="body-xs" sx={{fontWeight: 600, color: "#1e293b", fontSize: "11px"}}>
                            Ronald — {taskTypeLabel(task)}
                        </Typography>
                        {timestamp && (
                            <Typography level="body-xs" sx={{color: "#64748b", fontSize: "10px"}}>
                                {timestamp}
                            </Typography>
                        )}
                    </Box>
                    {actionLabel && (
                        <Box sx={{mt: 0.5, display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5}}>
                            <Box
                                component="span"
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    borderRadius: "9999px",
                                    px: 0.75,
                                    py: "2px",
                                    fontSize: "10px",
                                    fontWeight: 700,
                                    letterSpacing: "0.04em",
                                    bgcolor: "#f3e8ff",
                                    color: "#7c3aed",
                                }}
                            >
                                ⚡ {actionLabel}
                            </Box>
                        </Box>
                    )}
                    {task.cancel_reason && (
                        <Typography level="body-xs" sx={{mt: 0.5, color: "#475569", fontSize: "11px"}}>
                            {task.cancel_reason}
                        </Typography>
                    )}
                    {showIntervention && (
                        <Typography level="body-xs" sx={{mt: 0.5, color: "#b45309", fontSize: "11px"}}>
                            {task.intervention_reason}
                        </Typography>
                    )}
                </Box>

                {/* Right line */}
                <Box sx={{flex: 1, height: "1px", bgcolor: "#e2e8f0"}}/>
            </Box>
        </Box>
    );
}
